import { useCallback, useEffect, useRef, useState } from 'react';
import swal from 'sweetalert';
import { Loader2, MessageSquare, SendHorizontal } from 'lucide-react';
import { Alert } from '@material-ui/lab';

import Chat from '../Chat';
import Tooltip from '../shared/Tooltip';
import commentsService, { events } from '../../domain/services/solicitation/comments.service';
import commentsRealtime from '../../domain/services/solicitation/comments.realtime.service';
import loginService from '../../domain/services/login.service';
import { stepsSolicitation } from '../../domain/constant/steps.solicitation.constant';
import useViewedRequestComments from '../../hooks/useViewedRequestComments';

function CommentsForm({ request, resolvePending }) {
    const [comments, setComments] = useState([]);
    const [draft, setDraft] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [sendError, setSendError] = useState('');
    const [
        showLastCommentCustomerRelasePopover,
        setShowLastCommentCustomerRelasePopover
    ] = useState(false);
    const historyRef = useRef(null);
    const inputRef = useRef(null);
    const requestRef = useRef(request.id);
    const loadRef = useRef(0);
    const sendingRef = useRef(false);
    const followLatestRef = useRef(true);
    const mountedRef = useRef(false);
    const readError = useViewedRequestComments(historyRef, comments);
    requestRef.current = request.id;

    const findComments = useCallback(async () => {
        if (!request.id) return;

        const load = ++loadRef.current;
        setIsLoading(true);
        setLoadError('');
        try {
            const response = await commentsService.find(request.id);
            if (load === loadRef.current && requestRef.current === request.id)
                setComments(response.data || []);
        } catch (error) {
            if (load === loadRef.current) setLoadError('Não foi possível carregar os comentários.');
        } finally {
            if (load === loadRef.current) setIsLoading(false);
        }
    }, [request.id]);

    useEffect(() => {
        mountedRef.current = true;
        setComments([]);
        setDraft('');
        setSendError('');
        setShowLastCommentCustomerRelasePopover(false);
        followLatestRef.current = true;
        findComments();
        events.on('onRefresh', findComments);
        return () => {
            mountedRef.current = false;
            loadRef.current += 1;
            events.removeListener('onRefresh', findComments);
        };
    }, [findComments]);

    useEffect(() => {
        const history = historyRef.current;
        if (history && followLatestRef.current) history.scrollTop = history.scrollHeight;
    }, [comments]);

    useEffect(() => {
        const input = inputRef.current;
        if (!input) return;
        input.style.height = 'auto';
        input.style.height = `${Math.min(input.scrollHeight, 128)}px`;
    }, [draft]);

    const requestFamilyId = request.mainId || request.id;
    useEffect(() => {
        if (!loginService.isCustomer()) return;
        return commentsRealtime.subscribe({
            onConnect: findComments,
            onChange: (event) => {
                if (`${event?.mainRequestId}` === `${requestFamilyId}`) findComments();
            }
        });
    }, [findComments, requestFamilyId]);

    const checkIsPending = () => {
        if (
            request.status !== stepsSolicitation.PENDING ||
            !loginService.isCustomer() ||
            request.isFinancialPending ||
            typeof resolvePending !== 'function'
        )
            return;

        swal({
            title: 'Resolver Pendências',
            text: 'Você já tratou todas as pendências existentes nesta solicitação?',
            icon: 'info',
            buttons: {
                confirm: { text: 'Sim', className: 'MuiButton-containedPrimary' },
                cancel: 'Não'
            }
        }).then((confirmed) => confirmed && resolvePending());
    };

    const handleSave = async (event) => {
        event.preventDefault();
        const description = draft.trim();
        const requestId = request.id;
        if (!description || !requestId || sendingRef.current) return;

        sendingRef.current = true;
        setIsSending(true);
        setSendError('');
        try {
            await commentsService.save({ description, requestId });
            if (!mountedRef.current || requestRef.current !== requestId) return;
            setDraft('');
            followLatestRef.current = true;
            await findComments();
            if (!mountedRef.current || requestRef.current !== requestId) return;
            setShowLastCommentCustomerRelasePopover(loginService.isFaciliter());
            checkIsPending();
        } catch (error) {
            if (mountedRef.current && requestRef.current === requestId)
                setSendError('Não foi possível enviar. Seu comentário foi preservado.');
        } finally {
            sendingRef.current = false;
            if (mountedRef.current) setIsSending(false);
        }
    };

    return (
        <section
            aria-label="Conversa da solicitação"
            className="flex flex-col w-full min-w-0 h-[480px] md:h-[560px] max-h-[70vh] md:max-h-[75vh] min-h-[320px] border border-gray-200 rounded-lg overflow-hidden bg-white"
        >
            <header className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 bg-gray-50 shrink-0">
                <div className="flex items-center gap-2 min-w-0 break-words">
                    <MessageSquare size={18} className="text-primary shrink-0" />
                    <h2 className="m-0 text-sm font-semibold leading-5">Comentários</h2>
                </div>
            </header>
            <div
                ref={historyRef}
                className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden overscroll-contain p-3 md:p-4 bg-gray-50"
                onScroll={(event) => {
                    const element = event.currentTarget;
                    followLatestRef.current =
                        element.scrollHeight - element.scrollTop - element.clientHeight < 80;
                }}
            >
                {loadError && <Alert severity="error">{loadError}</Alert>}
                {readError && <Alert severity="warning">
                    Não foi possível confirmar a leitura. O aviso permanece no sininho.
                </Alert>}
                <Chat
                    messages={comments}
                    onRefresh={findComments}
                    isLoading={isLoading && !comments.length}
                    showLastCommentCustomerRelasePopover={showLastCommentCustomerRelasePopover}
                    setShowLastCommentCustomerRelasePopover={
                        setShowLastCommentCustomerRelasePopover
                    }
                />
            </div>
            <form
                className="p-3 border-t border-gray-200 bg-gray-50 shrink-0"
                onSubmit={handleSave}
            >
                {sendError && (
                    <Alert severity="error" style={{ marginBottom: 8 }}>
                        {sendError}
                    </Alert>
                )}
                <div
                    role="group"
                    aria-label="Mensagem do comentário"
                    className="flex items-end gap-2 min-w-0 rounded-lg border border-gray-300 bg-white p-1 focus-within:ring-2 focus-within:ring-primary"
                >
                    <textarea
                        ref={inputRef}
                        id="commentText"
                        aria-label="Escreva seu comentário"
                        placeholder="Escreva uma mensagem..."
                        rows={1}
                        className="flex-1 min-w-0 max-h-32 resize-none border-0 bg-transparent px-3 py-2 text-sm leading-6 focus:outline-none focus:ring-0 disabled:opacity-60"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        disabled={isSending || !request.id}
                    />
                    <Tooltip title="Enviar comentário">
                        <span className="flex items-center justify-center shrink-0 p-1">
                            <button
                                type="submit"
                                aria-label="Enviar comentário"
                                className="flex items-center justify-center w-8 h-8 shrink-0 rounded-full bg-primary text-white transition-colors hover:brightness-95 focus-visible:ring-2 focus-visible:ring-primary disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                                disabled={isSending || !draft.trim() || !request.id}
                            >
                                {isSending ? (
                                    <Loader2
                                        size={18}
                                        className="animate-spin"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <SendHorizontal size={18} aria-hidden="true" />
                                )}
                            </button>
                        </span>
                    </Tooltip>
                </div>
            </form>
        </section>
    );
}

export default CommentsForm;
